import { classifyKey } from 'services/imports/categoryRules';

export * from 'services/imports/categoryRules';

/**
 * 'unverified' is only assigned later by extraction (never by parseMarkdown).
 */
export type LineStatus = 'ok' | 'unknown-key' | 'manual' | 'malformed' | 'phase-2' | 'unverified';
export type LineSource = { file: string; page: number; line: string };
export type ImportTarget = { kind: 'new' } | { kind: 'existing'; id: string };
export type MarkdownLine = {
  lineNo: number;
  /** Stable row id (`r<n>`) written as `<!-- id: r<n> -->`; null for malformed lines. */
  id: string | null;
  key: string;
  value: string;
  src: LineSource | null;
  status: LineStatus;
  raw: string;
};
export type ParsedMarkdown = { target: ImportTarget | null; lines: MarkdownLine[] };
export type DataLineInput = { key: string; value: string; src?: LineSource | null; id?: string | null };
export type BuildMarkdownInput = { name: string; target: ImportTarget | null; lines: readonly DataLineInput[] };

type Section = 'target' | 'data' | 'other';

const SECTION_BY_HEADING = new Map<string, Section>([
  ['target', 'target'],
  ['data', 'data'],
]);

const HEADING_RE = /^#{1,2}\s+(.+?)\s*$/;
const DATA_LINE_RE = /^\s*-\s+([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)\s*:\s*(.*)$/s;
const SRC_MARKER = '<!-- src: ';
const SRC_TAIL_RE = /^<!-- src: (\{.*\}) -->\s*$/s;
const SRC_LINE_RE = /^L[1-9][0-9]*$/;
const TARGET_LINE_RE = /^\s*-\s+order\s*:\s*(.*?)\s*$/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const escapeNoteHtml = (text: string): string => text
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

/** Single line, trimmed, and never able to close an HTML comment. */
const toOneLine = (text: string): string => text
  .replace(/\r\n|[\n\r\u2028\u2029]/g, ' ')
  .trim()
  .replace(/-->/g, '-- >');

export const serializeSrc = (src: LineSource): string => JSON.stringify({ file: src.file, page: src.page, line: src.line })
  .replace(/</g, '\\u003c')
  .replace(/>/g, '\\u003e')
  .replace(/&/g, '\\u0026')
  .replace(/\u2028/g, '\\u2028')
  .replace(/\u2029/g, '\\u2029');

const ID_COMMENT = (id: string): string => `<!-- id: ${id} -->`;

/** One `- key: value` data line, with its row id and source comment when given. */
export const formatDataLine = ({ key, value, src, id }: DataLineInput): string => {
  const head = `- ${toOneLine(key)}:`;
  const body = toOneLine(value);
  const withValue = body === '' ? head : `${head} ${body}`;
  const withId = id ? `${withValue} ${ID_COMMENT(id)}` : withValue;
  return src ? `${withId} <!-- src: ${serializeSrc(src)} -->` : withId;
};

const buildTargetValue = (target: ImportTarget | null): string => {
  if (target === null) return '';
  switch (target.kind) {
    case 'new': return 'new';
    case 'existing': return toOneLine(target.id);
    default: {
      const unreachable: never = target;
      return unreachable;
    }
  }
};

export const buildMarkdown = ({ name, target, lines }: BuildMarkdownInput): string => {
  const targetValue = buildTargetValue(target);
  const targetLine = targetValue === '' ? '- order:' : `- order: ${targetValue}`;
  return [
    `# Imports - ${toOneLine(name)}`,
    '',
    '## Target',
    targetLine,
    '',
    '## Data',
    ...lines.map(formatDataLine),
    '',
  ].join('\n');
};

const parseSrcJson = (json: string): LineSource | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  if (!('file' in parsed) || !('page' in parsed) || !('line' in parsed)) return null;
  const { file, page, line } = parsed;
  if (typeof file !== 'string' || file === '') return null;
  if (typeof page !== 'number' || !Number.isSafeInteger(page) || page <= 0) return null;
  if (typeof line !== 'string' || !SRC_LINE_RE.test(line)) return null;
  return { file, page, line };
};

/** Splits a trailing src comment off the value text; src is rebuilt, never passed through. */
const extractSrc = (text: string): { value: string; src: LineSource | null } => {
  const idx = text.lastIndexOf(SRC_MARKER);
  if (idx < 0) return { value: text.trim(), src: null };
  const tail = SRC_TAIL_RE.exec(text.slice(idx));
  if (!tail) return { value: text.trim(), src: null };
  return { value: text.slice(0, idx).trim(), src: parseSrcJson(tail[1]) };
};

/**
 * Status precedence: malformed > unknown-key > phase-2 > manual (no src) > ok.
 */
const classifyLine = (key: string, src: LineSource | null): LineStatus => {
  const rule = classifyKey(key);
  if (rule === null) return 'unknown-key';
  if (rule.phase === 2) return 'phase-2';
  return src === null ? 'manual' : 'ok';
};

const ID_TAIL_RE = /\s*<!-- id: ([A-Za-z0-9_-]+) -->\s*$/;

/** Splits a trailing row id off the value text. */
const extractId = (text: string): { value: string; id: string | null } => {
  const match = ID_TAIL_RE.exec(text);
  if (!match) return { value: text, id: null };
  return { value: text.slice(0, match.index).trim(), id: match[1] };
};

const parseDataLine = (raw: string, lineNo: number): MarkdownLine => {
  const match = DATA_LINE_RE.exec(raw);
  if (!match) return { lineNo, id: null, key: '', value: raw.trim(), src: null, status: 'malformed', raw };
  const key = match[1];
  const { value: withId, src } = extractSrc(match[2]);
  const { value, id } = extractId(withId);
  return { lineNo, id, key, value, src, status: classifyLine(key, src), raw };
};

const parseTarget = (targetValues: readonly string[]): ImportTarget | null => {
  if (targetValues.length !== 1) return null;
  const value = targetValues[0];
  if (value.toLowerCase() === 'new') return { kind: 'new' };
  return UUID_RE.test(value) ? { kind: 'existing', id: value } : null;
};

export const parseMarkdown = (text: string): ParsedMarkdown => {
  const lines: MarkdownLine[] = [];
  const targetValues: string[] = [];
  let section: Section = 'other';
  text.split(/\r\n|\n|\r/).forEach((raw, index) => {
    if (raw.trim() === '') return;
    const heading = HEADING_RE.exec(raw);
    if (heading) {
      section = SECTION_BY_HEADING.get(heading[1].trim().toLowerCase()) ?? 'other';
      return;
    }
    if (section === 'data') {
      lines.push(parseDataLine(raw, index + 1));
      return;
    }
    if (section === 'target') {
      const order = TARGET_LINE_RE.exec(raw);
      if (order) targetValues.push(order[1]);
    }
  });
  return { target: parseTarget(targetValues), lines };
};
